import { useState, useEffect } from 'react';
import ModalContainer from '../shared/ModalContainer';
import AccessSelector from '../shared/AccessSelector';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import { useNotifications } from '../../contexts/NotificationContext';

export default function CreateProductModal({ isOpen, onClose, onProductCreated }) {
  const { fetchNotifications } = useNotifications();
  const [formData, setFormData] = useState({
    name: '',
    version: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Role and user data
  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [roleAccessLevels, setRoleAccessLevels] = useState({});
  const [userAccessLevels, setUserAccessLevels] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch available roles and users when modal opens
  useEffect(() => {
    if (isOpen) {
      loadRolesAndUsers();
    }
  }, [isOpen]);

  const loadRolesAndUsers = async () => {
    try {
      setLoading(true);
      // Fetch all roles
      const rolesResponse = await adminApi.users.getAllRoles();
      setAvailableRoles(rolesResponse.roles || []);

      // Fetch all users
      const usersResponse = await adminApi.users.getOverview();
      setAvailableUsers(usersResponse.users || []);
    } catch (error) {
      console.error('Failed to load roles and users:', error);
      toast.error('Failed to load roles and users');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleId)) {
        newSet.delete(roleId);
        // Remove access level when unchecked
        setRoleAccessLevels(prev => {
          const newLevels = { ...prev };
          delete newLevels[roleId];
          return newLevels;
        });
      } else {
        newSet.add(roleId);
        // Default to read access when checked
        setRoleAccessLevels(prev => ({ ...prev, [roleId]: 'read' }));
      }
      return newSet;
    });
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
        // Remove access level when unchecked
        setUserAccessLevels(prev => {
          const newLevels = { ...prev };
          delete newLevels[userId];
          return newLevels;
        });
      } else {
        newSet.add(userId);
        // Default to read access when checked
        setUserAccessLevels(prev => ({ ...prev, [userId]: 'read' }));
      }
      return newSet;
    });
  };

  const handleRoleAccessLevelChange = (roleId, level) => {
    setRoleAccessLevels(prev => ({ ...prev, [roleId]: level }));
  };

  const handleUserAccessLevelChange = (userId, level) => {
    setUserAccessLevels(prev => ({ ...prev, [userId]: level }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      // Build role assignments
      const role_assignments = Array.from(selectedRoles).map(roleId => ({
        role_id: roleId,
        access_level: roleAccessLevels[roleId] || 'read'
      }));

      // Build user assignments
      const user_assignments = Array.from(selectedUsers).map(userId => ({
        user_id: userId,
        access_level: userAccessLevels[userId] || 'read'
      }));

      const productData = {
        name: formData.name.trim(),
        version: formData.version.trim() || null,
        role_assignments,
        user_assignments
      };

      const response = await adminApi.products.create(productData);
      toast.success(response.message || 'Product created successfully');

      // Refresh notifications to clear any "no_products" notifications
      await fetchNotifications({ limit: 50 });

      onProductCreated?.(response.product);
      handleClose();
    } catch (error) {
      console.error('Failed to create product:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to create product';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', version: '' });
    setSelectedRoles(new Set());
    setSelectedUsers(new Set());
    setRoleAccessLevels({});
    setUserAccessLevels({});
    setErrors({});
    onClose();
  };

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg" style={{ color: 'var(--color-text-on-accent)' }}>
                      <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <span>Create New Product</span>
        </div>
      }
      isProcessing={isSubmitting}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            style={{ color: 'var(--color-text-on-accent)' }}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-400 border-t-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <span>Create Product</span>
            )}
          </button>
        </div>
      }
    >
      {loading ? (
        <div className="p-12 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-purple-500"></div>
        </div>
      ) : (
        <div className="p-6 space-y-5">
          {/* Product Name and Version in same row */}
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <label className="text-sm font-semibold text-gray-200 mb-2 block">
                  Product Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter product name"
                  disabled={isSubmitting}
                  className={`w-full px-3.5 py-2.5 bg-gray-800/40 border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-gray-800/60 disabled:opacity-50 transition-colors ${
                    errors.name ? 'border-red-500/70' : 'border-gray-600/50'
                  }`}
                />
                {errors.name && <p className="text-sm text-red-400 mt-1">{errors.name}</p>}
              </div>

              <div className="col-span-1">
                <label className="text-sm font-medium text-gray-300 mb-2 block">
                  Version
                </label>
                <input
                  type="text"
                  value={formData.version}
                  onChange={(e) => handleChange('version', e.target.value)}
                  placeholder="e.g., 1.0.0"
                  disabled={isSubmitting}
                  className="w-full px-3.5 py-2.5 bg-gray-800/40 border border-gray-600/50 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50 focus:bg-gray-800/60 disabled:opacity-50 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Access Selector */}
          <AccessSelector
            roles={availableRoles}
            items={availableUsers}
            selectedRoles={selectedRoles}
            selectedItems={selectedUsers}
            itemAccessLevels={userAccessLevels}
            onRoleToggle={handleRoleToggle}
            onItemToggle={handleUserToggle}
            onItemAccessLevelChange={handleUserAccessLevelChange}
            itemLabel="Users"
            disabled={isSubmitting}
          />
        </div>
      )}
    </ModalContainer>
  );
}
